import { FC, useCallback, useEffect, useState } from 'react';

import { CheckoutConfirmationHeadline } from 'src/pages/Checkout/components/Headline';
import { CheckoutConfirmationSharing } from 'src/pages/Checkout/components/Sharing';
import { CheckoutConfirmationGatedLiveStream } from 'src/pages/Checkout/components/GatedLiveStream';
import { CheckoutConfirmationDelivery } from 'src/pages/Checkout/components/Delivery';
import { CheckoutConfirmationGiftAid } from 'src/pages/Checkout/components/GiftAid';
import { CheckoutConfirmationOrganizationTaxNotice } from 'src/pages/Checkout/components/OrganizationTaxNotice';
import { CheckoutConfirmationViralSharing } from 'src/pages/Checkout/components/ViralSharing';
import { CheckoutConfirmationButtons } from 'src/pages/Checkout/components/Buttons';
import { UiLoader } from 'src/components/UI/loaders';
import {
  CheckoutConfirmationResponseType,
  CheckoutConfirmationSavePreferencesRequestType,
} from 'src/models/Checkout/Confirmation';
import { getDOMElement } from 'src/utils/DOM';
import { useRequest } from 'src/utils/hooks/useRequest';
import { ChildrenPropType } from 'src/utils/types';
import { StateFormReturnType } from 'src/utils/stateForm';
import { apiSavePreferences } from 'src/api/checkout';
import { hardRedirect } from 'src/utils/hardRedirect';
import { CheckoutConfirmationContactOrganizer } from 'src/pages/Checkout/components/ContactOrganizer';
import { GoogleTagManager } from 'src/utils/GoogleTagManager';

type Props = {
  model: CheckoutConfirmationResponseType | null;
  loading: boolean;
  children: ChildrenPropType;
  formProps: StateFormReturnType<CheckoutConfirmationSavePreferencesRequestType>;
};

export const CheckoutConfirmationLayout: FC<Props> = ({ model, loading, children, formProps }) => {
  const { apiCaller } = useRequest();

  const [savePreferencesLoading, setSavePreferencesLoading] = useState(false);

  const savePreferences = useCallback(() => {
    setSavePreferencesLoading(true);

    formProps.onSubmit((data) => {
      apiCaller(apiSavePreferences, data)
        .then(() => {
          hardRedirect(model?.ContinueUrl || '');
        })
        .catch(() => {
          setSavePreferencesLoading(false);
        });
    })();
  }, [apiCaller, formProps, model]);

  useEffect(() => {
    GoogleTagManager.pushEvent({
      event: 'pageview',
    });
  }, []);

  useEffect(() => {
    const beforeprint = () => {
      const elementForPrinting = getDOMElement('#printable');

      if (elementForPrinting) {
        const tempWrapper = document.createElement('div');
        tempWrapper.className = 'printable';
        tempWrapper.appendChild(elementForPrinting.cloneNode(true));
        document.body.appendChild(tempWrapper);
      }
    };

    const afterprint = () => {
      getDOMElement('.printable')?.remove();
    };

    window.addEventListener('beforeprint', beforeprint);
    window.addEventListener('afterprint', afterprint);

    return () => {
      window.removeEventListener('beforeprint', beforeprint);
      window.removeEventListener('afterprint', afterprint);
    };
  }, []);

  return model && !loading ? (
    <div id="printable">
      <div id="confirmation-page" className="contribution-sections public-page">
        <div className="no-color-header SecondaryBrandColor">
          <div className="container">
            <span className="no-color-header-page-title SecondaryTextColor">{model.Header}</span>
          </div>
        </div>
        <div id="wrapper" className="container">
          {model.HeadlinesSubsection.IsVisible && <CheckoutConfirmationHeadline model={model.HeadlinesSubsection} />}
          {model.SharingSubsection.IsVisible && <CheckoutConfirmationSharing model={model.SharingSubsection} />}
          <section>
            <div className="container">
              {children}

              {model.GatedLiveStreamSubsection.IsVisible && (
                <CheckoutConfirmationGatedLiveStream model={model.GatedLiveStreamSubsection} />
              )}
              {model.DeliverySubsection.IsVisible && <CheckoutConfirmationDelivery model={model.DeliverySubsection} />}
              {model.GiftAidSubsection.IsVisible && <CheckoutConfirmationGiftAid model={model.GiftAidSubsection} />}
              {model.OrganizationTaxNoticeSubsection.IsVisible && (
                <CheckoutConfirmationOrganizationTaxNotice model={model.OrganizationTaxNoticeSubsection} />
              )}
              {model.ViralSharingSubsection.IsVisible && (
                <CheckoutConfirmationViralSharing model={model.ViralSharingSubsection} />
              )}
              <CheckoutConfirmationButtons
                model={model.ButtonsSubsection}
                savePreferences={savePreferences}
                savePreferencesLoading={savePreferencesLoading}
              />
            </div>
          </section>
        </div>
        <CheckoutConfirmationContactOrganizer permanenturl={model.PermanentUrl} />
      </div>
    </div>
  ) : (
    <UiLoader fullScreenCentered />
  );
};
